import { inject } from 'mobx-react';
import { Link } from 'react-router-dom';
import * as React from 'react';

import { ClickTracker } from 'bernie-analytics';
import { ContextStore, RouteStore } from 'bernie-core';

import { useLocalization } from '@shared-ui/localization-context';

import { CurrentHotelStore } from 'stores/current-hotel-store';
import { MultiItemStore } from 'stores/multi-item-store';

import { UitkCardAloha, UitkCardAlohaContentSection } from 'uitk-react-cards';
import { UitkLink } from 'uitk-react-link';
import { UitkSpacing } from 'uitk-react-spacing';
import { UitkText } from 'uitk-react-text';

import { AvailabilityWizardInputErrors } from 'components/infosite/rooms-and-rates/availability-wizard';

const LinkTracked = ClickTracker(Link);

export interface ListingsErrorProps {
  multiItem?: MultiItemStore;
  context?: ContextStore;
  currentHotel?: CurrentHotelStore;
  routeStore?: RouteStore;

  inputErrors?: AvailabilityWizardInputErrors;
}

const getGenericErrorMessage = (brandName: string, endDate: string, formatText: (key: string, ...args: string[]) => string): string =>
  endDate
    ? formatText('hotels.infosite.availabilityWizard.noAvailableRooms.global.text', brandName)
    : formatText('hotels.search.validation.invalidToDate.text');

const hasOffers = (currentHotel?: CurrentHotelStore): boolean => {
  const { bexApiPropertyInfoOffersResponse, offers } = currentHotel;

  const listings = bexApiPropertyInfoOffersResponse?.offers?.listings;
  const verticalListings =
    bexApiPropertyInfoOffersResponse?.offers?.experimental?.ratePlanCategorizationExperimentalFeature.verticalListings;

  const noOffers = !(offers?.rooms?.length > 0) && !(listings?.length > 0) && !(verticalListings?.length > 0);

  return !(noOffers && !offers?.recommendedOffer);
};

export const ListingsError: React.FunctionComponent<ListingsErrorProps> = inject(
  'context',
  'currentHotel',
  'multiItem',
  'routeStore'
)(({ context, currentHotel, multiItem, routeStore, inputErrors = {} }) => {
  const { formatText } = useLocalization();
  const { loadingOffers } = currentHotel;

  const { errorMessage, errorMessagingModel } = currentHotel?.offers || {};

  const { startDate, endDate, adults, children } = inputErrors;
  let errorMsg = multiItem?.errorMessage || startDate || endDate || adults || children;
  const brandName = context?.site?.name;

  if (!errorMsg && loadingOffers === false && !hasOffers(currentHotel)) {
    const availabilityErrorMessagingStaticMessage = errorMessagingModel?.staticMessage;

    errorMsg =
      errorMessage ||
      availabilityErrorMessagingStaticMessage ||
      getGenericErrorMessage(brandName, currentHotel?.offerSearchData?.endDate, formatText);
  }

  if (!errorMsg) {
    return null;
  }

  // Temporary until the correct url is returned from the bff.
  // The href value for the error message is only the queryparam, not the
  // whole path.
  // When fixed, no need for the pathname and search anymore
  // TODO: add bff PR
  const linkDestination = errorMessagingModel?.actionLink?.includes('?')
    ? { to: errorMessagingModel.actionLink }
    : { pathname: routeStore.pathname, search: errorMessagingModel?.actionLink };

  return (
    <UitkSpacing padding={{ blockend: 'three' }}>
      <div id="availability-wizard">
        <UitkCardAloha>
          <UitkCardAlohaContentSection>
            <h4 id="availability-message" className="uitk-type-regular">
              <UitkText theme="negative" size={300}>
                {errorMsg}
                {errorMessagingModel && (
                  <>
                    {' '}
                    <UitkLink inline={true}>
                      <LinkTracked
                        rfrr={`unavailCTA.${errorMessagingModel.actionRfrr}`}
                        linkName="Recover from availability search criteria error"
                        to={linkDestination}
                      >
                        {errorMessagingModel.actionableMessage}
                      </LinkTracked>
                    </UitkLink>
                  </>
                )}
              </UitkText>
            </h4>
          </UitkCardAlohaContentSection>
        </UitkCardAloha>
      </div>
    </UitkSpacing>
  );
});
